import { useDispatch } from "react-redux";
import { ProposalData } from "../../../../api/gms/proposal/proposal-api-state";
import { useTransferOwnershipDataState } from "../../../../hooks/data-state/use-transferOwnership-data-state";
import { statusStyleTransferOwner } from "../../../../pages/gms-department/transfer-ownership-proposal-page";
import OwnerProposalTbody from "./owner-proposal-tbody";
import OwnerProposalTHead from "./owner-proposal-thead";
import { SelectedProposal, setSearchOwnershipProposal, setSelectedProposals } from "../../../../features/transfer-ownership/transfer-ownership-slice";
import LoadingData from "../../../ui/loading-data";
import LoadingState from "../../../ui/loadibg-state";
import ErrorState from "../../../ui/error-state";

interface Props {
    fromUserId: null | number;
    inputStyle: string;
    availableProposals: ProposalData[]
    selectedProposals: SelectedProposal[]
    ownershipSearchValue: string;
    isLoading: boolean;
    isError: boolean;
}

export default function OwnerProposalList({
    fromUserId,
    inputStyle,
    availableProposals,
    selectedProposals,
    ownershipSearchValue,
    isLoading,
    isError,
}: Props) {

    const dispatch = useDispatch()
    const allVisibleSelected =
        availableProposals.length > 0 &&
        availableProposals.every((proposal) => selectedProposals.some((selected) => selected.id === proposal.application_id));
    
        const handleProposalToggle = (data: SelectedProposal) => {
            dispatch(setSelectedProposals({
                groupName: data.groupName,
                id: data.id
            }))
        }

        const handleSelectAllProposal = () => {
            if (availableProposals.length === 0) return;
            dispatch(setSelectedProposals(
                availableProposals.map((item) => ({
                    id: item.application_id,
                    groupName: item.group_name,
                }))
            ))
        }

    return (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-100 bg-[#eef6fc] px-4 py-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-2">
                        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#186cb2] text-[10px] font-bold text-white">
                            3
                        </div>
                        <div>
                            <h2 className="text-xs font-semibold text-gray-800">
                                Select Proposals
                            </h2>
                            <p className="text-[10px] text-gray-500">
                                Select one or multiple proposals to transfer.
                            </p>
                        </div>
                    </div>
                    {fromUserId !== 0 && (
                        <div className="rounded-full border border-[#186cb2]/20 bg-white px-2.5 py-1 text-[10px] font-semibold text-[#186cb2]">
                            {selectedProposals.length} selected
                        </div>
                    )}
                </div>
            </div>
            <div className="p-4">
                {(fromUserId === 0 || fromUserId === null) && (!isLoading || !isError) ? (
                    <div className="flex min-h-[220px] flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 text-center">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-400">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v-3m0-4h.01M5.5 20h13a2 2 0 001.73-3L13.73 4a2 2 0 00-3.46 0L3.77 17a2 2 0 001.73 3Z" />
                            </svg>
                        </div>
                        <p className="mt-3 text-xs font-semibold text-gray-700">
                            Select a current owner first
                        </p>
                        <p className="mt-1 max-w-sm text-[10px] leading-4 text-gray-500">
                            Available proposals will appear here after selecting the user who currently owns them.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="mb-3 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 sm:flex sm:items-center lg:justify-between">
                            <div className="relative min-w-0 sm:w-full sm:max-w-md sm:flex-1">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400">
                                    <circle cx="11" cy="11" r="7" />
                                    <path strokeLinecap="round" d="m16.5 16.5 4 4" />
                                </svg>
                                <input
                                    type="text"
                                    value={ownershipSearchValue}
                                    onChange={(event) => dispatch(setSearchOwnershipProposal(event.target.value))}
                                    placeholder="Search group name, product, type, status, or prepared by..."
                                    className={`${inputStyle} min-w-0 pl-9`}
                                />
                            </div>
                            <button
                                type="button"
                                onClick={handleSelectAllProposal}
                                disabled={availableProposals.length === 0}
                                className="whitespace-nowrap rounded-lg border border-[#186cb2]/25 bg-white px-3 py-2.5 text-[10px] font-semibold text-[#186cb2] transition hover:bg-[#186cb2]/5 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                {allVisibleSelected ? 'Unselect All' : 'Select All'}
                            </button>
                        </div>
                        {availableProposals.length === 0 ? (
                            <div className="flex min-h-[220px] flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 text-center">
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-400">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
                                        <circle cx="11" cy="11" r="7" />
                                        <path strokeLinecap="round" d="m16.5 16.5 4 4" />
                                    </svg>
                                </div>
                                <p className="mt-3 text-xs font-semibold text-gray-700">
                                    No proposals found
                                </p>
                                <p className="mt-1 text-[10px] text-gray-500">
                                    This owner has no proposals matching your search.
                                </p>
                            </div>
                        ) : (
                            <>
                                {isLoading ? (
                                    <div className="py-3">
                                        <LoadingState/>
                                    </div>
                                ) : isError ? (
                                    <ErrorState/>
                                ) : (
                                    <>
                                        <div className="w-full max-w-full overflow-x-auto rounded-lg border border-gray-200"> 
                                            <table className="w-full min-w-[850px] border-collapse">
                                                <OwnerProposalTHead
                                                    checked={allVisibleSelected}
                                                    onChange={handleSelectAllProposal}
                                                />
                                                <OwnerProposalTbody
                                                    proposals={availableProposals}
                                                    selectedProposals={selectedProposals}
                                                    handleProposalToggle={handleProposalToggle}
                                                />
                                            </table>
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}